import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BookOpenIcon, ClockIcon, SearchIcon, XIcon } from 'lucide-react';
import { programDomains, programTemplates, programsIndexSlug } from '../data/programTemplates';
import { degreeInterestOptions, focusOptions } from '../data/degrees';
import { degreeProgramCombos } from '../utils/degreeSlugs';
import { usePageMeta } from '../hooks/usePageMeta';
import { LegalFooter } from '../components/LegalFooter';
const ANY = '';

/**
 * Searchable index of every /program-information/ page.
 *
 * Programs are filterable by credential and workforce domain, and searchable
 * across name, hero copy, features, and career roles — so someone searching
 * "phlebotomy" or "ICD-10" lands on the right program even when that term is
 * not in the title.
 */
export function ProgramInformationIndexPage() {
  usePageMeta('Online Program Information | Online College Guide', 'Search accredited online degree and diploma programs by credential and field. Compare length, credits, and career outcomes for each program.');
  const [query, setQuery] = useState('');
  const [credential, setCredential] = useState(ANY);
  const [domain, setDomain] = useState(ANY);
  const credentials = useMemo(() => Array.from(new Set(programTemplates.map(p => p.credential))).sort(), []);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return programTemplates.filter(program => {
      if (credential && program.credential !== credential) return false;
      if (domain && program.domainSlug !== domain) return false;
      if (!q) return true;
      return [program.name, program.credential, program.heroSubhead, program.metaDescription, ...program.features, ...program.careerRoles, ...(program.concentrations?.map(c => c.name) ?? [])].join(' ').toLowerCase().includes(q);
    });
  }, [query, credential, domain]);

  // Generated degree × program combinations, searched separately from the
  // curated programs above — there are several hundred of them.
  const [comboQuery, setComboQuery] = useState('');
  const [comboDegree, setComboDegree] = useState(ANY);
  const [comboArea, setComboArea] = useState(ANY);
  const [comboLimit, setComboLimit] = useState(60);
  const comboResults = useMemo(() => {
    const q = comboQuery.trim().toLowerCase();
    return degreeProgramCombos.filter(combo => {
      if (comboDegree && combo.degreeInterest !== comboDegree) return false;
      if (comboArea && combo.areaStudy !== comboArea) return false;
      if (!q) return true;
      return `${combo.programLabel} ${combo.areaLabel} ${combo.degreeLabel}`.toLowerCase().includes(q);
    });
  }, [comboQuery, comboDegree, comboArea]);

  // A narrowed search should start from the top, not mid-way down a list the
  // visitor already expanded.
  useEffect(() => {
    setComboLimit(60);
  }, [comboQuery, comboDegree, comboArea]);
  const isFiltered = Boolean(query.trim() || credential || domain);
  const clearFilters = () => {
    setQuery('');
    setCredential(ANY);
    setDomain(ANY);
  };
  const domainName = (slug: string) => programDomains.find(d => d.slug === slug)?.name ?? '';
  const selectClass = 'w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson transition-colors';
  return <main className="bg-white" data-id="element-2495" data-mp="2satoh0naero">
      {/* Hero */}
      <header className="hero-bg" data-id="element-2496" data-mp="3txajjs9vr5hv">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-2497" data-mp="3tw98vows0l0o">
          <nav aria-label="Breadcrumb" className="mb-6" data-id="element-2498" data-mp="3oypjbsumoltf">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-white/70" data-id="element-2499" data-mp="3p0affmwlccte">
              <li data-id="element-2500" data-mp="2ijoue740t3wc">
                <Link to="/" className="hover:text-white transition-colors" data-id="element-2501" data-mp="2ifrb9lhg0ccg">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-2502" data-mp="67kflzsqoy7j">/</li>
              <li className="text-white/90" data-id="element-2503" data-mp="66j5bxjjsfqv">Program Information</li>
            </ol>
          </nav>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5 max-w-3xl" data-id="element-2504" data-mp="azvgp5z7uq1o">
            Program Information
          </h1>
          <p className="text-lg text-white/80 leading-relaxed max-w-2xl" data-id="element-2505" data-mp="17y4px0dvu1w">
            Detailed breakdowns of what each program covers, how long it takes,
            and where it leads — so you can compare before you commit.
          </p>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-id="element-2506" data-mp="f05xzesgl2vu">
        {/* Search + filters */}
        <div className="mb-10" data-id="element-2507" data-mp="jyz1qfhy1fit">
          <label htmlFor="program-search" className="sr-only" data-id="element-2508" data-mp="jy7bzlb12c0g">
            Search programs
          </label>
          <div className="relative max-w-xl mb-4" data-id="element-2509" data-mp="2zplftk9p9h9c">
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" aria-hidden="true" data-id="element-2510" data-mp="34p7xc5wk9jny" />
            <input id="program-search" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search programs — try “coding”, “patient care”, or “claims”" className="w-full pl-10 pr-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson transition-colors" data-id="element-2511" data-mp="1mto237dflt4d" />
          </div>

          <div className="grid sm:grid-cols-2 gap-4 max-w-xl" data-id="element-2512" data-mp="z3touop7a2jl">
            <div data-id="element-2513" data-mp="1cu6ycvhqppb1">
              <label htmlFor="filter-credential" className="block text-xs font-semibold text-gray-600 mb-1.5" data-id="element-2514" data-mp="1cuoyw4bfqpor">
                Credential
              </label>
              <select id="filter-credential" value={credential} onChange={e => setCredential(e.target.value)} className={selectClass} data-id="element-2515" data-mp="kc1j94f7k2v2">
                <option value={ANY} data-id="element-2516" data-mp="2fg91liqnixqx">All credentials</option>
                {credentials.map(c => <option key={c} value={c} data-id="element-2517" data-mp="2kdst3kcd170t">
                    {c}
                  </option>)}
              </select>
            </div>
            <div data-id="element-2518" data-mp="2u5opmqyssp2x">
              <label htmlFor="filter-domain" className="block text-xs font-semibold text-gray-600 mb-1.5" data-id="element-2519" data-mp="2u43up08kn44j">
                Field
              </label>
              <select id="filter-domain" value={domain} onChange={e => setDomain(e.target.value)} className={selectClass} data-id="element-2520" data-mp="21hj3q0w0cu1i">
                <option value={ANY} data-id="element-2521" data-mp="2bfp3ocy97q3q">All fields</option>
                {programDomains.map(d => <option key={d.slug} value={d.slug} data-id="element-2522" data-mp="3hu809d5bldy6">
                    {d.name}
                  </option>)}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-4 mt-4 text-sm text-gray-500" data-id="element-2523" data-mp="1b5phu0s412ut">
            <span aria-live="polite" data-id="element-2524" data-mp="1674gtz51af5b">
              {results.length} {results.length === 1 ? 'program' : 'programs'}
            </span>
            {isFiltered && <button type="button" onClick={clearFilters} className="inline-flex items-center gap-1 text-navy font-medium hover:underline" data-id="element-2525" data-mp="11el1awmsd9xv">
                <XIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-2526" data-mp="rgf4gtfu6tzv" />
                Clear filters
              </button>}
            <Link to={`/program-information/${programsIndexSlug}`} className="text-navy font-medium hover:underline" data-id="element-2527" data-mp="iha4diommsll">
              Browse by field
            </Link>
          </div>
        </div>

        {/* Results */}
        {results.length === 0 ? <div className="border border-gray-200 rounded-xl py-16 px-6 text-center" data-id="element-2528" data-mp="27wqbksizq48u">
            <h2 className="text-lg font-bold text-navy mb-2" data-id="element-2529" data-mp="1grxdhcpsc9c3">
              No programs match that search
            </h2>
            <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto" data-id="element-2530" data-mp="1lpp6ka2621hv">
              Try a broader term, or tell us your goals and we'll suggest
              programs that fit.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3" data-id="element-2531" data-mp="1c05qtd9r2nr8">
              <button type="button" onClick={clearFilters} className="px-5 py-2.5 text-sm font-semibold text-navy border border-navy rounded-lg hover:bg-navy/5 transition-colors duration-200" data-id="element-2532" data-mp="y1ftbwj0qm2c">
                Show all programs
              </button>
              <Link to="/request-information" className="inline-flex items-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-2.5 px-6 rounded-lg text-sm transition-colors duration-200" data-id="element-2533" data-mp="t19vfym5o3bt">
                Get Matched Now
                <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-2534" data-mp="bshcjqbttn4k" />
              </Link>
            </div>
          </div> : <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2535" data-mp="6uz0vs6fctsv">
            {results.map(program => <li key={program.slug} data-id="element-2536" data-mp="ljv7rlwqw0rw">
                <Link to={`/program-information/${program.slug}`} className="group flex flex-col h-full border border-gray-200 rounded-xl p-6 hover:border-navy hover:shadow-md transition-[border-color,box-shadow] duration-200 focus:outline-none focus:ring-2 focus:ring-crimson" data-id="element-2537" data-mp="gs3przrnn3dd">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-crimson mb-2" data-id="element-2538" data-mp="14g3fqxh56pqw">
                    {program.credential}
                  </span>
                  <h2 className="text-lg font-bold text-navy leading-snug mb-2 group-hover:underline" data-id="element-2539" data-mp="3689h9uocu6rd">
                    {program.name}
                  </h2>
                  <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 mb-5" data-id="element-2540" data-mp="31bsup4wt00ge">
                    {program.heroSubhead}
                  </p>
                  <dl className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-gray-500 mb-5" data-id="element-2541" data-mp="1hva0isdfw9xk">
                    <div className="flex items-center gap-1.5" data-id="element-2542" data-mp="1cvos7rcdqp86">
                      <ClockIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-2543" data-mp="1cy9tof8rn35e" />
                      <dt className="sr-only" data-id="element-2544" data-mp="18woayd9jvuna">Length</dt>
                      <dd data-id="element-2545" data-mp="25ldvycgh53dh">{program.weeks} weeks</dd>
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-2546" data-mp="20mb09imdw9i6">
                      <BookOpenIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-2547" data-mp="20mt1ktnwldwy" />
                      <dt className="sr-only" data-id="element-2548" data-mp="1ruitbhwkjiic">Credits</dt>
                      <dd data-id="element-2549" data-mp="1rrwmxllez91p">{program.credits} credits</dd>
                    </div>
                  </dl>
                  <span className="mt-auto pt-4 border-t border-gray-100 flex items-center justify-between gap-3" data-id="element-2550" data-mp="2odgghyw8l3t0">
                    <span className="text-xs text-gray-500" data-id="element-2551" data-mp="62iuh7n9wfie">
                      {domainName(program.domainSlug)}
                    </span>
                    <ArrowRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-navy" aria-hidden="true" data-id="element-2552" data-mp="b25a1nqkq7ud" />
                  </span>
                </Link>
              </li>)}
          </ul>}
      </div>

      {/* Every degree level × program combination. Separate from the curated
          programs above, because these are generated pages and there are
          several hundred of them. */}
      <section aria-labelledby="combos-heading" className="border-t border-gray-200 bg-light" data-id="element-2553" data-mp="12sa1g6cr3v53">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-2554" data-mp="17se6n087l3g0">
          <h2 id="combos-heading" className="text-2xl font-bold text-navy mb-2" data-id="element-2555" data-mp="1cqg1eiezyr7b">
            All degree and program combinations
          </h2>
          <p className="text-sm text-gray-600 max-w-2xl mb-6" data-id="element-2556" data-mp="38rhrihvi7rxb">
            Every degree level paired with every field of study, each with its
            own page and match form.
          </p>

          <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mb-5" data-id="element-2557" data-mp="3ilqfcs9lawp5">
            <div data-id="element-2558" data-mp="3ds5jp6vgyet4">
              <label htmlFor="combo-search" className="block text-xs font-semibold text-gray-600 mb-1.5" data-id="element-2559" data-mp="3drm6u4koegly">
                Search
              </label>
              <input id="combo-search" type="search" value={comboQuery} onChange={e => setComboQuery(e.target.value)} placeholder="e.g. nursing" className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson transition-colors" data-id="element-2560" data-mp="3nigu9c9nrm8z" />
            </div>
            <div data-id="element-2561" data-mp="hmtmdievypet">
              <label htmlFor="combo-degree" className="block text-xs font-semibold text-gray-600 mb-1.5" data-id="element-2562" data-mp="hoefr4pgs2b3">
                Degree level
              </label>
              <select id="combo-degree" value={comboDegree} onChange={e => setComboDegree(e.target.value)} className={selectClass} data-id="element-2563" data-mp="7ws6hdwosdaz">
                <option value={ANY} data-id="element-2564" data-mp="3v0olkh2qdy6t">All levels</option>
                {degreeInterestOptions.map(d => <option key={d.value} value={d.value} data-id="element-2565" data-mp="3x9nwosc8ebb">
                    {d.type}
                  </option>)}
              </select>
            </div>
            <div data-id="element-2566" data-mp="3q47ylq79ppvb">
              <label htmlFor="combo-area" className="block text-xs font-semibold text-gray-600 mb-1.5" data-id="element-2567" data-mp="3q3olqnwh5ro5">
                Area of study
              </label>
              <select id="combo-area" value={comboArea} onChange={e => setComboArea(e.target.value)} className={selectClass} data-id="element-2568" data-mp="1ob0hcb83gy6s">
                <option value={ANY} data-id="element-2569" data-mp="3gb8kpkmwaq5h">All areas</option>
                {focusOptions.map(f => <option key={f.value} value={f.value} data-id="element-2570" data-mp="3bgcrih5x9wqs">
                    {f.type}
                  </option>)}
              </select>
            </div>
          </div>

          <p className="text-sm text-gray-500 mb-5" aria-live="polite" data-id="element-2571" data-mp="31hnb6stockcn">
            {comboResults.length.toLocaleString()}{' '}
            {comboResults.length === 1 ? 'combination' : 'combinations'}
          </p>

          {comboResults.length === 0 ? <p className="text-sm text-gray-600" data-id="element-2572" data-mp="2np79pwgf8udu">
              No combinations match that search.
            </p> : <>
              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-2573" data-mp="2iot0wc6zxger">
                {comboResults.slice(0, comboLimit).map(item => <li key={item.slug} data-id="element-2574" data-mp="23ncihhvvleol">
                    <Link to={`/program-information/${item.slug}`} className="group flex items-center justify-between gap-3 bg-white border border-gray-200 rounded-lg px-4 py-3 hover:border-navy hover:shadow-sm transition-[border-color,box-shadow] duration-200 focus:outline-none focus:ring-2 focus:ring-crimson" data-id="element-2575" data-mp="23mb69a34im5a">
                      <span className="min-w-0" data-id="element-2576" data-mp="1kxe98ygcdfjd">
                        <span className="block text-sm font-semibold text-navy truncate" data-id="element-2577" data-mp="1pv62pwwmx9po">
                          {item.programLabel}
                        </span>
                        <span className="block text-xs text-gray-500 truncate" data-id="element-2578" data-mp="1usy3lgf9c660">
                          {item.degreeLabel} · {item.areaLabel}
                        </span>
                      </span>
                      <ArrowRightIcon className="w-4 h-4 flex-shrink-0 text-gray-400 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-navy" aria-hidden="true" data-id="element-2579" data-mp="125brtkvvlv82" />
                    </Link>
                  </li>)}
              </ul>
              {comboResults.length > comboLimit && <button type="button" onClick={() => setComboLimit(n => n + 60)} className="mt-6 px-5 py-2.5 text-sm font-semibold text-navy border border-navy rounded-lg hover:bg-navy/5 transition-colors duration-200" data-id="element-2580" data-mp="18t0hi67e5s28">
                  Show more
                </button>}
            </>}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-navy" data-id="element-2581" data-mp="l4heo67427hj">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center" data-id="element-2582" data-mp="l3eo5zdpa725">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3" data-id="element-2583" data-mp="21heimuts14i0">
            Not sure which program fits?
          </h2>
          <p className="text-white/80 max-w-xl mx-auto mb-7" data-id="element-2584" data-mp="1wj39ynf7x01d">
            Answer a few questions about your goals and schedule and we'll
            connect you with accredited schools offering these programs. No
            cost, no obligation.
          </p>
          <Link to="/request-information" className="inline-flex items-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-3.5 px-8 rounded-lg text-sm transition-colors duration-200" data-id="element-2585" data-mp="1v2z7xig1i032">
            Get Matched Now
            <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-2586" data-mp="2mvpvl4jnqggp" />
          </Link>
        </div>
      </section>

      <LegalFooter data-id="element-2587" data-mp="2e11pxda66wrd" />
    </main>;
}